import { ref, computed } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import type { PropertyResponse, PropertyError } from '~/types/property';

const searchQuery = ref('');
const searchResults = ref<any[]>([]);
const isSearching = ref(false);
const searchError = ref<string | null>(null);

export const usePortfolioSearch = () => {
  const authStore = useAuthStore();

  const performSearch = async (query: string) => {
    if (!query.trim()) {
      searchResults.value = [];
      return;
    }

    if (!authStore.token) {
      searchError.value = 'User not authenticated';
      return;
    }

    try {
      isSearching.value = true;
      searchError.value = null;

      const response = await $fetch<PropertyResponse | PropertyError>('/api/portfolio/search', {
        method: 'GET',
        params: {
          q: query,
          limit: 10,
        },
        headers: {
          Authorization: `Bearer ${authStore.token}`,
        },
      });

      if (response.success && response.data) {
        searchResults.value = Array.isArray(response.data) ? response.data : [];
      } else {
        searchResults.value = [];
        if (!response.success) {
          searchError.value = (response as PropertyError).error || 'Search failed';
        }
      }
    } catch (error) {
      console.error('Error performing search:', error);
      searchResults.value = [];
      searchError.value = 'Failed to search properties';
    } finally {
      isSearching.value = false;
    }
  };

  const debouncedSearch = useDebounceFn(performSearch, 300);

  const clearSearch = () => {
    searchQuery.value = '';
    searchResults.value = [];
    searchError.value = null;
  };

  const hasResults = computed(() => searchResults.value.length > 0);
  const showDropdown = computed(() => {
    return searchQuery.value.trim().length > 0 && (hasResults.value || isSearching.value);
  });

  return {
    searchQuery,
    searchResults,
    isSearching,
    searchError,
    hasResults,
    showDropdown,
    performSearch: debouncedSearch,
    clearSearch,
  };
};

export default usePortfolioSearch;
